import { requiredIf } from 'vuelidate/lib/validators'

const isOrm = function () {
  return this.entity != null && this.entity.orm != null
}

// ORM-specific validation must fire ONLY while the user is submitting the quote
// at the ORM page (Send Quote / Send Tax Invoice). `isOrmValidation` is set
// true by validateJobStartEndForOrm() during those flows and reset afterwards
// (and intentionally left true on a FAILED submit so the user keeps seeing the
// errors). Gating every ORM rule behind this keeps ORM errors — and their
// inline messages — out of normal Save / autosave on an ORM quote.
const isOrmSubmit = function () {
  return isOrm.call(this) && this.isOrmValidation === true
}

export const ormValidationMessages = {
  claimNo: {
    ormRequired: 'Claim Number is required by ORM',
    ormMaxLength: 'Claim Number cannot exceed 15 characters'
  },
  companyName: {
    ormMaxLength: 'Company Name cannot exceed 45 characters'
  },
  paintCode: {
    ormMaxLength: 'Paint Code cannot exceed 10 characters'
  },
  rego: {
    ormMaxLength: 'Registration No. cannot exceed 9 characters'
  }
}

export const ormValidationRules = {
  ormHeaderGroup: [
    'vehicle.bodyId2',
    'vehicle.buildMonth',
    'vehicle.buildYear',
    'vehicle.transmissionType',
    'vehicle.paintCode',
    'vehicle.rego',
    'customer.lastname',
    'customer.firstname',
    'customer.companyName',
    'entity.claimNo',
    'entity.damageSeverity'
  ],
  ormSummaryGroup: [
    'entity.jobStart',
    'entity.jobEnd',
    'entity.quoter',
    'entity.odometer',
    'vehicle.colour',
    'entity.refuseBookRepairReason'
  ],
  repairLateGroup: [
    'entity.repairLateReason'
  ],
  estimateJobStartEnd: [
    'entity.jobStart',
    'entity.jobEnd'
  ],
  entity: {
    odometer: {
      required: requiredIf(isOrmSubmit),
      validOdometer: function (value) {
        if (!isOrmSubmit.call(this)) return true
        return value > 0 && value <= 9999999
      }
    },
    claimNo: {
      ormRequired: requiredIf(isOrmSubmit),
      ormMaxLength: function (value) {
        if (!isOrmSubmit.call(this)) return true
        if (!value) return true
        return String(value).length <= 15
      }
    },
    jobStart: {
      required: requiredIf(isOrmSubmit),
      bothRequired: function () {
        if (!isOrmSubmit.call(this)) return true
        return !!(this.entity.jobStart && this.entity.jobEnd)
      },
      validJobStart: function (value) {
        if (!isOrmSubmit.call(this)) return true
        if (value && this.entity.jobEnd) {
          return value <= this.entity.jobEnd
        }
        return true
      }
    },
    jobEnd: {
      required: requiredIf(isOrmSubmit),
      bothRequired: function () {
        if (!isOrmSubmit.call(this)) return true
        return !!(this.entity.jobStart && this.entity.jobEnd)
      },
      validJobEnd: function (value) {
        if (!isOrmSubmit.call(this)) return true
        if (value && this.entity.jobStart) {
          return value >= this.entity.jobStart
        }
        return true
      }
    },
    damageSeverity: {
      required: requiredIf(function () {
        return isOrmSubmit.call(this) && this.insurer && this.insurer.damageSeverity
      }),
      validDamageSeverity: function (value) {
        if (!isOrmSubmit.call(this)) return true
        return value > 0 || !(this.insurer && this.insurer.damageSeverity)
      }
    },
    refuseBookRepairReason: {
      required: requiredIf(function () {
        if (!isOrmSubmit.call(this)) return false
        if (this.entity.isRepairBooked == null || this.entity.isRepairBooked == undefined) return false
        return !this.entity.isRepairBooked && this.entity.repx != null
      })
    },
    repairLateReason: {
      required: requiredIf(function () {
        if (!isOrmSubmit.call(this)) return false
        return this.entity.repx != null && this.entity.isArriveForInspection != null && this.entity.isArriveForInspection == false
      })
    }
  },
  customer: {
    companyName: {
      ormMaxLength: function (value) {
        if (!isOrmSubmit.call(this)) return true
        if (!value) return true
        return String(value).length <= 45
      }
    }
  },
  vehicle: {
    bodyId2: {
      required: requiredIf(isOrmSubmit)
    },
    buildMonth: {
      required: requiredIf(isOrmSubmit)
    },
    buildYear: {
      required: requiredIf(isOrmSubmit)
    },
    paintCode: {
      ormMaxLength: function (value) {
        if (!isOrmSubmit.call(this)) return true
        if (!value) return true
        return String(value).length <= 10
      }
    },
    rego: {
      ormMaxLength: function (value) {
        if (!isOrmSubmit.call(this)) return true
        if (!value) return true
        return String(value).length <= 9
      }
    }
  }
}

export default {
  data() {
    return {
      isOrmValidation: false
    }
  },
  methods: {
    validateJobStartEndForOrm() {
      this.isOrmValidation = true
      this.$v.entity.jobStart.$reset()
      this.$v.entity.jobEnd.$reset()
      this.$v.entity.jobStart.$touch()
      this.$v.entity.jobEnd.$touch()
    },
    touchOrmHeaderValidators() {
      this.$v.ormHeaderGroup.$touch()
      this.$v.entity.claimNo.$touch()
      this.$v.entity.damageSeverity.$touch()
      this.$v.vehicle.bodyId2.$touch()
      this.$v.vehicle.buildMonth.$touch()
      this.$v.vehicle.buildYear.$touch()
      this.$v.vehicle.transmissionType.$touch()
      this.$v.vehicle.paintCode.$touch()
      this.$v.vehicle.rego.$touch()
      this.$v.customer.companyName.$touch()
      this.$v.customer.firstname.$touch()
      this.$v.customer.lastname.$touch()
    },
    touchOrmSummaryValidators() {
      this.$v.ormSummaryGroup.$touch()
      this.$v.entity.quoter.$touch()
      this.$v.entity.odometer.$touch()
      this.$v.entity.refuseBookRepairReason.$touch()
      this.$v.vehicle.colour.$touch()
      this.$v.vehicle.paintCode.$touch()
      this.$v.customer.companyName.$touch()
      this.$v.customer.firstname.$touch()
      this.$v.customer.lastname.$touch()
    },
    resetOrmValidation() {
      this.isOrmValidation = false
    }
  }
}
